import type React from "react"
import { Table, Tag, Tooltip, Button, Popconfirm, type TableProps, type TablePaginationConfig } from "antd"
import { EyeOutlined, DeleteOutlined, FireOutlined } from "@ant-design/icons"
import type { AiWordItem } from "@/services/log/aiWordApi"
import { useTableScroll } from "@/hooks/useTableScroll"
import { WEB_URL } from "@/configs/config"
import { formatDateTime } from "@/utils/dateUtils"
import { TABLE_ACTION_COLUMN_WIDTH, TABLE_ACTION_CELL_CLASSNAME } from "@/constants/table"
import "@/styles/table.full.css"

interface AiWordTableProps {
  data: AiWordItem[]
  loading: boolean
  onViewDetail: (record: AiWordItem) => void
  onDelete: (id: number) => void
  pagination?: TableProps<AiWordItem>["pagination"]
  onChange?: TableProps<AiWordItem>["onChange"]
  sortField?: string
  sortOrder?: "ASC" | "DESC"
}

const getPosColor = (pos?: string) => {
  if (!pos) return "default"
  const normalized = pos.trim().toLowerCase()
  if (normalized.startsWith("noun")) return "blue"
  if (normalized.startsWith("verb")) return "gold"
  if (normalized.startsWith("adj")) return "green"
  if (normalized.startsWith("adv")) return "purple"
  return "cyan"
}

export const AiWordTable: React.FC<AiWordTableProps> = ({
  data,
  loading,
  onViewDetail,
  onDelete,
  pagination,
  onChange,
  sortField = "search_count",
  sortOrder = "DESC",
}) => {
  const { scrollConfig, tableWrapperRef } = useTableScroll()

  const columns: TableProps<AiWordItem>["columns"] = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 75,
      align: "center",
      fixed: "left",
      render: (val: number) => <span className="font-mono text-gray-500 text-xs">#{val}</span>,
    },
    {
      title: "Từ vựng",
      dataIndex: "word",
      key: "word",
      width: 170,
      fixed: "left",
      render: (word: string, record: AiWordItem) => {
        const pos = record.data?.part_of_speech
        return (
          <div className="flex flex-col items-start gap-1">
            <a
              href={`${WEB_URL}/en-vi/dictionary/${record.slug || encodeURIComponent(word)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-blue-600 hover:text-blue-800 hover:underline"
              onClick={(e) => {
                e.stopPropagation()
              }}
            >
              {word}
            </a>
            {pos && (
              <Tag color={getPosColor(pos)} className="m-0 text-[11px] leading-tight">
                {pos}
              </Tag>
            )}
          </div>
        )
      },
    },
    {
      title: "Phiên âm",
      key: "ipa",
      width: 130,
      render: (_, record: AiWordItem) => {
        const ipa = record.data?.ipa
        return ipa ? (
          <span className="font-mono text-gray-700 text-xs">{ipa}</span>
        ) : (
          <span className="text-gray-400">—</span>
        )
      },
    },
    {
      title: "Nghĩa tiếng Việt",
      key: "meaning",
      minWidth: 200,
      ellipsis: { showTitle: true },
      render: (_, record: AiWordItem) => {
        const meaning = record.data?.meaning
        return meaning ? (
          <span className="text-gray-800">{meaning}</span>
        ) : (
          <span className="text-gray-400 italic">Chưa có nghĩa</span>
        )
      },
    },
    {
      title: "Lượt tìm kiếm",
      dataIndex: "search_count",
      key: "search_count",
      width: 140,
      align: "center",
      sorter: true,
      sortOrder: sortField === "search_count" ? (sortOrder === "ASC" ? "ascend" : "descend") : null,
      render: (count: number) => {
        const isHot = count >= 10
        return (
          <Tag
            color={isHot ? "error" : "blue"}
            icon={isHot ? <FireOutlined /> : undefined}
            className="font-bold font-mono text-xs"
          >
            {count}
          </Tag>
        )
      },
    },
    {
      title: "Lượt thêm",
      dataIndex: "add_count",
      key: "add_count",
      width: 120,
      align: "center",
      sorter: true,
      sortOrder: sortField === "add_count" ? (sortOrder === "ASC" ? "ascend" : "descend") : null,
      render: (count: number) => {
        const isHigh = (count ?? 0) >= 5
        return (
          <Tag color={isHigh ? "green" : "default"} className="font-bold font-mono text-xs">
            {count ?? 0}
          </Tag>
        )
      },
    },

    {
      title: "Thời gian tạo",
      dataIndex: "created_at",
      key: "created_at",
      width: 135,
      align: "center",
      sorter: true,
      sortOrder: sortField === "created_at" ? (sortOrder === "ASC" ? "ascend" : "descend") : null,
      render: (val: string) => (
        <span className="font-mono text-gray-600 text-xs">{formatDateTime(val, "dd/mm/yy hh:mm") || "—"}</span>
      ),
    },
    {
      title: "Thao tác",
      key: "action",
      width: TABLE_ACTION_COLUMN_WIDTH,
      align: "center",
      fixed: "right",
      render: (_, record: AiWordItem) => (
        <div className={TABLE_ACTION_CELL_CLASSNAME}>
          <Tooltip title="Xem chi tiết">
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined className="text-blue-500 hover:text-blue-600" />}
              onClick={() => onViewDetail(record)}
            />
          </Tooltip>

          <Tooltip title="Xóa từ này">
            <Popconfirm
              title="Xác nhận xóa từ vựng"
              description={`Bạn có chắc chắn muốn xóa từ "${record.word}" khỏi danh sách AI không?`}
              onConfirm={() => onDelete(record.id)}
              okText="Xóa"
              cancelText="Hủy"
              okButtonProps={{ danger: true }}
            >
              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined className="text-red-500 hover:text-red-600" />}
              />
            </Popconfirm>
          </Tooltip>
        </div>
      ),
    },
  ]

  return (
    <div className="min-h-0 min-w-0 flex-1" ref={tableWrapperRef}>
      <Table<AiWordItem>
        bordered
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        pagination={pagination as TablePaginationConfig}
        onChange={onChange}
        scroll={{ x: "100%", y: scrollConfig.y }}
        size="small"
        classNames={{
          root: "full-height-table",
        }}
        onRow={(record) => ({
          onDoubleClick: () => onViewDetail(record),
          className: "cursor-pointer hover:bg-blue-50/30",
        })}
      />
    </div>
  )
}

export default AiWordTable
